import { EllipsisVertical, X } from "lucide-react";
import {
  Children,
  Fragment,
  cloneElement,
  isValidElement,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type ReactElement,
  type ReactNode,
  type RefObject,
} from "react";
import { createPortal } from "react-dom";
import { Button, buttonClass } from "./button";
import { cn } from "./cn";

/* -------------------------------------------------------------------------- */
/*  Layering                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * The stacking order, in one place.
 *
 * It used to be improvised per component, and the improvisation collided: the
 * bulk-approve bar sat at `z-50` and the toast viewport at `z-[60]`, so the
 * "12 approved · Undo" toast — the one control that could reverse the action —
 * landed directly on top of the selection bar that triggered it.
 *
 * The order encodes what must be reachable over what. Menus sit above modals
 * because a menu opened *inside* a modal has to render above it. Toasts sit
 * above everything because they are how the app reports what just happened, and
 * an unreadable confirmation is worse than none.
 */
export const Z = {
  sticky: "z-10", // app header, sticky table headers
  actionBar: "z-30", // bulk-selection bars
  navDrawer: "z-40", // mobile nav
  overlay: "z-50", // modal + drawer scrim and panel
  popover: "z-60", // menus, comboboxes, notification panel
  toast: "z-70", // always last
} as const;

/* -------------------------------------------------------------------------- */
/*  Shared overlay behaviour                                                   */
/* -------------------------------------------------------------------------- */

const FOCUSABLE =
  'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),' +
  'textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

let scrollLocks = 0;

function lockScroll() {
  if (scrollLocks++ > 0) return;
  const gap = window.innerWidth - document.documentElement.clientWidth;
  document.body.style.overflow = "hidden";
  // Removing the scrollbar widens the page; without this the whole layout jumps
  // sideways the instant a dialog opens.
  if (gap > 0) document.body.style.paddingRight = `${gap}px`;
}

function unlockScroll() {
  if (--scrollLocks > 0) return;
  scrollLocks = 0;
  document.body.style.overflow = "";
  document.body.style.paddingRight = "";
}

/**
 * Escape-to-close, focus trap, focus restore, and scroll lock.
 *
 * None of this existed. `Modal` had no Escape handler, no trap, and no scroll
 * lock, so Tab walked straight out of an open dialog into the page behind it and
 * the background scrolled under your fingers on a phone. The review drawer was
 * worse — it could only be dismissed by clicking the backdrop.
 */
function useOverlayBehaviour(
  open: boolean,
  onClose: () => void,
  panelRef: RefObject<HTMLElement | null>
) {
  const restoreTo = useRef<HTMLElement | null>(null);

  // Callers pass an inline arrow, so `onClose` is a new function on every
  // render. Reading it through a ref keeps it out of the effect below, whose
  // setup *moves focus*: with `onClose` in the deps the effect re-ran on every
  // keystroke, so typing one character in a dialog field re-focused the panel's
  // first control — the close button — and typing was impossible. It also left
  // `restoreTo` pointing at whatever the teardown had just focused.
  const closeRef = useRef(onClose);
  useEffect(() => {
    closeRef.current = onClose;
  });

  useEffect(() => {
    if (!open) return;

    restoreTo.current = document.activeElement as HTMLElement | null;
    lockScroll();

    const panel = panelRef.current;
    // Focus the first control, or the panel itself when there is nothing to
    // focus — otherwise focus stays behind on the trigger and Escape, which is
    // bound to the panel's subtree, never fires.
    const first = panel?.querySelector<HTMLElement>(FOCUSABLE);
    (first ?? panel)?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        closeRef.current();
        return;
      }
      if (event.key !== "Tab" || !panel) return;

      const items = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null || el === document.activeElement
      );
      if (items.length === 0) {
        event.preventDefault();
        return;
      }
      const firstItem = items[0]!;
      const lastItem = items[items.length - 1]!;
      if (event.shiftKey && document.activeElement === firstItem) {
        event.preventDefault();
        lastItem.focus();
      } else if (!event.shiftKey && document.activeElement === lastItem) {
        event.preventDefault();
        firstItem.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
      unlockScroll();
      // Returning focus to whatever opened this is the difference between a
      // keyboard user carrying on and being dumped at the top of the document.
      restoreTo.current?.focus?.();
    };
  }, [open, panelRef]);
}

/* -------------------------------------------------------------------------- */
/*  Anchored positioning                                                       */
/* -------------------------------------------------------------------------- */

export interface AnchoredPosition {
  top: number;
  left?: number;
  right?: number;
}

/**
 * Position a portalled panel against a trigger, flipping up when it would
 * overflow the viewport and closing on scroll or resize.
 *
 * This logic existed in three near-identical copies — `ActionMenu`, the client
 * page's assignment popover, and the notification bell — each with its own
 * magic numbers and its own subtly different outside-click handling.
 *
 * `estimatedHeight` is a fallback, not a description: the panel is measured
 * whenever it is in the DOM, which is every case the app currently has.
 */
export function useAnchoredPosition({
  open,
  onClose,
  triggerRef,
  panelRef,
  estimatedHeight = 240,
  align = "right",
  gap = 6,
}: {
  open: boolean;
  onClose: () => void;
  triggerRef: RefObject<HTMLElement | null>;
  panelRef: RefObject<HTMLElement | null>;
  estimatedHeight?: number;
  align?: "left" | "right";
  gap?: number;
}) {
  const [position, setPosition] = useState<AnchoredPosition>({ top: 0, right: 0 });

  const measure = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const bounds = trigger.getBoundingClientRect();

    /*
     * The panel's own height, not the guess.
     *
     * Refs are attached before layout effects run and the panel is visible by
     * then, so by the time this measures there is nothing left to estimate —
     * and `offsetHeight` is layout, so the slide-down transform doesn't move
     * it. The estimate survives only as the fallback for a panel that isn't in
     * the DOM yet.
     *
     * A guess is not a harmless approximation here, because it is used twice:
     * once to decide whether to flip, and again as the distance to flip *by*.
     * A one-item menu guessed at 240px flipped when it had ample room, then
     * hung 240px above a 44px panel — most of a screen from the trigger that
     * opened it.
     */
    const height = panelRef.current?.offsetHeight || estimatedHeight;
    const spaceBelow = window.innerHeight - bounds.bottom - gap;
    const spaceAbove = bounds.top - gap;
    // Below is the default; flipping is for when it genuinely fits better up.
    const flip = height > spaceBelow && spaceAbove > spaceBelow;
    const top = flip ? Math.max(8, bounds.top - height - gap) : bounds.bottom + gap;
    setPosition(
      align === "right"
        ? { top, right: Math.max(8, window.innerWidth - bounds.right) }
        : { top, left: Math.max(8, bounds.left) }
    );
  }, [triggerRef, panelRef, estimatedHeight, align, gap]);

  useLayoutEffect(() => {
    if (open) measure();
  }, [open, measure]);

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (triggerRef.current?.contains(target)) return;
      if (panelRef.current?.contains(target)) return;
      onClose();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    const onScroll = (event: Event) => {
      // Only a scroll that moves the trigger strands the panel: the page, or a
      // container the trigger sits in. Anything else leaves the anchor where it
      // was — scrolling inside the panel, or an input scrolling its own text,
      // which a search box holding more than it can show does the moment focus
      // leaves it. That one used to shut a row's menu as it opened.
      const target = event.target;
      if (target instanceof Node && !target.contains(triggerRef.current)) return;
      onClose();
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    window.addEventListener("resize", onClose);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("resize", onClose);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [open, onClose, triggerRef, panelRef]);

  return position;
}

/* -------------------------------------------------------------------------- */
/*  Popover                                                                    */
/* -------------------------------------------------------------------------- */

export const menuItemClass =
  "flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-text " +
  "hover:bg-surface-hover disabled:opacity-50 disabled:pointer-events-none";

/** Back-compat alias for the old name. */
export const actionMenuItemClass = menuItemClass;

/**
 * The actions a menu would have held, when there are few enough not to need one.
 *
 * Callers style their children for the panel (`menuItemClass`: full width, flush
 * left), so collapsing has to restyle them. Dialog components take
 * `buttonClassName`, plain buttons take `className` — the clone sets whichever
 * the element already declares, which is what lets every existing call site
 * collapse without being touched.
 */
const inlineActionClass = buttonClass({ variant: "secondary", size: "sm" });

/**
 * Every real action inside a menu, with fragments flattened.
 *
 * `Children.toArray` drops the `false` a `{cond && <button/>}` leaves behind —
 * which is the whole point, since how many actions a row *has* is usually
 * conditional — but it counts a `<>…</>` as one child. Admin rows wrap pairs of
 * buttons in fragments, so without descending into them a three-action row
 * would look like a one-action row and collapse wrongly.
 */
function menuActions(node: ReactNode): ReactElement[] {
  const out: ReactElement[] = [];
  for (const child of Children.toArray(node)) {
    if (!isValidElement(child)) continue;
    if (child.type === Fragment) {
      out.push(...menuActions((child.props as { children?: ReactNode }).children));
    } else {
      out.push(child);
    }
  }
  return out;
}

/** Past this, the actions are a menu; at or below it, they are buttons. */
const INLINE_ACTION_LIMIT = 2;

export const menuPanelClass =
  "rounded-lg border border-line bg-surface-raised py-1 text-left shadow-e3";

export function ActionMenu({
  label,
  children,
  width = "w-48",
}: {
  label: string;
  children: ReactNode;
  width?: string;
}) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const panelId = useId();
  const close = useCallback(() => setOpen(false), []);
  const position = useAnchoredPosition({ open, onClose: close, triggerRef, panelRef });

  /**
   * Choosing an item closes the menu.
   *
   * `useAnchoredPosition` owns dismissal and deliberately ignores clicks inside
   * the panel, so nothing here ever closed on *activation*: the menu stayed up,
   * floating over the dialog it had just opened — `Z.popover` sits above
   * `Z.overlay`, because a menu opened inside a modal has to.
   *
   * On `click`, not `mousedown`: closing on mousedown applies `hidden` before
   * mouseup, so the item never receives a click and its action never runs. On
   * the bubble phase, so an item that must stay open can say so with
   * `event.stopPropagation()`.
   *
   * The containment check is not defensive. `EditButton` renders its `Modal`
   * among these children, and a portal moves the DOM but not the React tree, so
   * every click inside that dialog bubbles through here. Without the check,
   * clicking a field in the dialog would drag focus back to the trigger behind
   * it and typing would go nowhere.
   */
  function onPanelClick(event: ReactMouseEvent<HTMLDivElement>) {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (!panelRef.current?.contains(target)) return;
    if (!target.closest("button,a,[role='menuitem']")) return;

    // Move focus off the item first, synchronously. The item is about to become
    // `display:none`, and the dialog this click is opening captures its
    // focus-restore target in an effect — an effect here would run *after* that
    // one, since React flushes child effects first, so it has to happen now.
    triggerRef.current?.focus();
    setOpen(false);
  }

  /*
   * One or two actions are not a menu.
   *
   * A popover costs two clicks and hides what it holds; that is worth paying to
   * keep a long list out of a row, and not worth paying to hide a single "Edit".
   * The count is of the actions this *row* actually renders, not of what the
   * caller wrote — a menu whose items are conditional (an admin cannot
   * deactivate themselves; an archived client cannot take a new project) is a
   * menu whose size differs per row, and each row gets the treatment it earns.
   *
   * After the hooks, never before: this returns early, and the rules of hooks
   * do not care what a component returns, only that it calls the same hooks.
   */
  const actions = menuActions(children);
  if (actions.length <= INLINE_ACTION_LIMIT) {
    return (
      <div className="flex items-center justify-end gap-2">
        {actions.map((action, index) =>
          cloneElement(action, {
            key: action.key ?? index,
            ...("buttonClassName" in (action.props as object)
              ? { buttonClassName: inlineActionClass }
              : { className: inlineActionClass }),
          } as Partial<typeof action.props> & { key: string | number })
        )}
      </div>
    );
  }

  return (
    <>
      <Button
        ref={triggerRef}
        variant="secondary"
        size="md"
        iconOnly
        onClick={() => setOpen((current) => !current)}
        aria-label={label}
        aria-expanded={open}
        aria-controls={panelId}
      >
        <EllipsisVertical className="h-4 w-4" aria-hidden="true" />
      </Button>
      {/* Portalled unconditionally and merely hidden when closed, never
          unmounted: an `EditButton` renders its `Modal` among these children, so
          tearing the panel down would take the dialog it just opened with it. */}
      {createPortal(
        // It said `role="menu"`, and it was not one: the children are whatever the
        // caller passes — ordinary `<button className={menuItemClass}>`, sometimes an
        // `EditButton` that renders a dialog — never `role="menuitem"`, and there is
        // no arrow-key roving focus. A menu that announces itself as a menu and then
        // offers no menu items is worse than a plain popover, so it is a plain
        // popover: a disclosure, named by `aria-expanded` + `aria-controls`, holding
        // buttons that are already reachable by Tab and activated by Enter.
        //
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- `onPanelClick` is event delegation, not an affordance: it closes the panel *after* one of those child buttons is activated, and a native button already turns Enter and Space into the click this reads. There is nothing here for a keyboard user to reach that Tab does not already reach.
        <div
          ref={panelRef}
          id={panelId}
          aria-hidden={!open}
          onClick={onPanelClick}
          style={{ top: position.top, right: position.right, left: position.left }}
          className={cn(
            "fixed",
            Z.popover,
            width,
            menuPanelClass,
            open ? "animate-slide-down" : "hidden"
          )}
        >
          {children}
        </div>,
        document.body
      )}
    </>
  );
}

/* -------------------------------------------------------------------------- */
/*  Modal                                                                      */
/* -------------------------------------------------------------------------- */

const MODAL_SIZE = {
  sm: "max-w-sm",
  md: "max-w-lg",
  lg: "max-w-2xl",
  xl: "max-w-4xl",
} as const;

export function Modal({
  open,
  onClose,
  title,
  description,
  footer,
  size = "md",
  children,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: ReactNode;
  /** Pinned below the scroll area, so the primary action never scrolls away. */
  footer?: ReactNode;
  size?: keyof typeof MODAL_SIZE;
  children: ReactNode;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  useOverlayBehaviour(open, onClose, panelRef);

  if (!open) return null;

  return createPortal(
    <div className={cn("fixed inset-0", Z.overlay)}>
      <div
        className="absolute inset-0 bg-ink-950/50 animate-fade-in backdrop-blur-[2px]"
        onClick={onClose}
        aria-hidden="true"
      />
      <div className="absolute inset-0 grid place-items-center overflow-y-auto p-4">
        {/* A dialog that is only a `div` is not announced as one, and nothing
            can scope to it — neither a screen reader nor a test can tell it
            apart from the page it is covering. */}
        <div
          ref={panelRef}
          role="dialog"
          aria-modal="true"
          aria-label={title}
          tabIndex={-1}
          className={cn(
            // `dvh`: on a phone `vh` is the height with the browser's toolbars hidden,
            // so a tall dialog's footer — its primary action — sat under them.
            "relative flex max-h-[calc(100dvh-2rem)] w-full flex-col rounded-xl border border-line",
            "bg-surface-raised shadow-e4 animate-scale-in outline-none",
            MODAL_SIZE[size]
          )}
        >
          <div className="flex items-start justify-between gap-4 border-b border-line px-5 py-4">
            <div className="min-w-0">
              <h2 className="text-md font-semibold text-text">{title}</h2>
              {description && (
                <p className="mt-0.5 text-sm text-subtle">{description}</p>
              )}
            </div>
            <Button
              variant="ghost"
              size="sm"
              iconOnly
              onClick={onClose}
              aria-label="Close"
              className="-mr-1.5 -mt-0.5"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </Button>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto scrollbar-thin p-5">
            {children}
          </div>
          {footer && (
            <div className="flex flex-wrap items-center justify-end gap-2 border-t border-line px-5 py-3.5">
              {footer}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

/* -------------------------------------------------------------------------- */
/*  Drawer                                                                     */
/* -------------------------------------------------------------------------- */

const DRAWER_SIZE = {
  sm: "sm:max-w-md",
  md: "sm:max-w-xl",
  lg: "sm:max-w-3xl",
} as const;

/**
 * A side panel.
 *
 * Review flows want a drawer, not a centred dialog: you are reading a week of
 * entries *against* the queue you came from, and a modal that covers the queue
 * breaks that comparison. The old review panel was a hand-rolled fixed div with
 * no Escape, no focus trap and no scroll lock; this is the same shape with the
 * behaviour it should always have had.
 *
 * Full-bleed below `sm` — a 24rem panel inset on a 390px phone is just a modal
 * with extra steps.
 */
export function Drawer({
  open,
  onClose,
  title,
  description,
  footer,
  size = "md",
  children,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: ReactNode;
  footer?: ReactNode;
  size?: keyof typeof DRAWER_SIZE;
  children: ReactNode;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  useOverlayBehaviour(open, onClose, panelRef);

  if (!open) return null;

  return createPortal(
    <div className={cn("fixed inset-0", Z.overlay)}>
      <div
        className="absolute inset-0 bg-ink-950/50 animate-fade-in backdrop-blur-[2px]"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        className={cn(
          "absolute inset-y-0 right-0 flex w-full flex-col border-l border-line",
          "bg-surface shadow-e4 animate-slide-in-right outline-none",
          DRAWER_SIZE[size]
        )}
      >
        <div className="flex items-start justify-between gap-4 border-b border-line px-5 py-4">
          <div className="min-w-0">
            <h2 className="text-md font-semibold text-text">{title}</h2>
            {description && (
              <p className="mt-0.5 truncate text-sm text-subtle">{description}</p>
            )}
          </div>
          <Button
            variant="ghost"
            size="sm"
            iconOnly
            onClick={onClose}
            aria-label="Close"
            className="-mr-1.5 -mt-0.5"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </Button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto scrollbar-thin p-5">
          {children}
        </div>
        {footer && (
          <div className="flex items-center justify-end gap-2 border-t border-line bg-surface px-5 py-3.5">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
